import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import MobileLanding from "../mobile/MobileLanding";
import { ContactForm } from "../components/Shared";

export default function Landing() {
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 768px)");
    const handleChange = (e) => setIsMobile(e.matches);

    setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  if (isMobile) {
    return <MobileLanding />;
  }

  const handleContactClick = (e) => {
    const section = document.getElementById("contact");
    if (section) {
      e.preventDefault();
      section.scrollIntoView({ behavior: "smooth" });
    } else {
      // If contact section is not on the present page view, fallback to standard routing
      e.preventDefault();
      navigate("/#contact");
    }
  };

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      {/* ========================= */}
      {/* TOP BRANDING / HERO SECTION */}
      {/* ========================= */}
      <section className="relative pt-8 pb-10 bg-white overflow-hidden">
        {/* SOFT GLOW */}
        <div className="absolute top-[-150px] left-[-100px] w-[500px] h-[500px] bg-blue-200/20 rounded-full blur-[120px]" />
        <div className="absolute top-[-100px] right-[-100px] w-[400px] h-[400px] bg-cyan-200/20 rounded-full blur-[120px]" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col items-center justify-center text-center">
            {/* LOGO */}
            <img
              src="/logo.png"
              alt="BAJABAR Logo"
              className="h-44 sm:h-52 md:h-64 lg:h-72 w-auto object-contain scale-110 drop-shadow-2xl"
            />

            {/* COMPANY NAME */}
            <h1 className="mt-2 text-[34px] sm:text-[44px] md:text-[58px] lg:text-[64px] font-black tracking-[-2px] text-slate-900 leading-none">
              BAJABAR INCORPORATION FZE
            </h1>

            {/* TAGLINE */}
            <div className="mt-5 inline-flex flex-col md:flex-row items-center gap-4 px-5 md:px-8 py-4 rounded-[28px] bg-white border border-slate-200 shadow-[0_15px_50px_rgba(0,0,0,0.06)]">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-gradient-to-br from-blue-700 to-cyan-500 text-white flex items-center justify-center shadow-xl">
                <i className="fas fa-shield-alt text-xl md:text-2xl" />
              </div>

              <div className="text-center md:text-left">
                <div className="text-[11px] md:text-sm uppercase tracking-[0.3em] text-slate-500 font-black">
                  Your Trusted Partner
                </div>

                <div className="mt-1 text-[22px] sm:text-[28px] md:text-[36px] font-black leading-tight">
                  <span className="text-slate-900">Your Business Solutions</span>
                  <span className="mx-2 text-blue-700">in</span>
                  <span className="bg-gradient-to-r from-blue-700 to-cyan-500 bg-clip-text text-transparent">
                    UAE
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========================= */}
      {/* DIVISIONS SECTION */}
      {/* ========================= */}
      <section className="relative pt-4 md:pt-5 pb-10 md:pb-12 bg-gradient-to-b from-white to-slate-50 overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
          {/* CARDS */}
          <div className="grid lg:grid-cols-2 gap-5 lg:gap-6">
            {/* LOGISTICS CARD */}
            <div className="group bg-white rounded-[32px] overflow-hidden border border-slate-100 shadow-[0_20px_60px_rgba(0,0,0,0.06)] hover:-translate-y-2 transition-all duration-700">
              <div className="relative h-44 md:h-52 overflow-hidden">
                <img
                  src="/backgroundlanding.png"
                  alt="Logistics"
                  className="w-full h-full object-cover group-hover:scale-105 transition duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-slate-900/10 to-transparent" />
                <div className="absolute top-4 left-4 px-4 py-2 rounded-full bg-white/95 backdrop-blur-xl shadow-lg text-blue-700 font-bold text-xs md:text-sm">
                  Freight Division
                </div>
              </div>

              <div className="p-5 md:p-6">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-700 to-cyan-500 text-white grid place-items-center text-lg shadow-xl">
                  <i className="fas fa-shipping-fast" />
                </div>
                <h3 className="mt-4 text-[30px] md:text-[38px] font-black text-slate-900 leading-none">
                  Freight & Logistics
                </h3>
                <p className="mt-4 text-slate-600 leading-[1.8] text-[15px] md:text-[17px]">
                  International freight forwarding, customs clearance and end-to-end logistics services worldwide.
                </p>

                <div className="mt-5 space-y-3">
                  {[
                    "Customs Clearance",
                    "Air, Sea & Land Freight Services",
                    "15+ Years of Industry Experience",
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[9px]">
                        <i className="fas fa-check" />
                      </div>
                      <span className="text-slate-700 font-semibold text-sm md:text-base">
                        {item}
                      </span>
                    </div>
                  ))}
                </div>

                <Link
                  to="/logistics"
                  className="inline-flex items-center justify-center gap-3 mt-6 w-full px-6 py-4 rounded-2xl bg-gradient-to-r from-blue-700 to-cyan-500 text-white font-bold hover:scale-[1.02] transition-all duration-300 shadow-xl text-sm md:text-base"
                >
                  Explore Logistics
                  <i className="fas fa-arrow-right" />
                </Link>
              </div>
            </div>

            {/* TRADING CARD */}
            <div className="group bg-white rounded-[32px] overflow-hidden border border-slate-100 shadow-[0_20px_60px_rgba(0,0,0,0.06)] hover:-translate-y-2 transition-all duration-700">
              <div className="relative h-44 md:h-52 overflow-hidden">
                <img
                  src="/tradingbackground.png"
                  alt="Trading"
                  className="w-full h-full object-cover group-hover:scale-105 transition duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-slate-900/10 to-transparent" />
                <div className="absolute top-4 left-4 px-4 py-2 rounded-full bg-white/95 backdrop-blur-xl shadow-lg text-blue-700 font-bold text-xs md:text-sm">
                  Trading Division
                </div>
              </div>

              <div className="p-5 md:p-6">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-700 to-cyan-500 text-white grid place-items-center text-lg shadow-xl">
                  <i className="fas fa-bolt" />
                </div>
                <h3 className="mt-4 text-[30px] md:text-[38px] font-black text-slate-900 leading-none">
                  General Trading
                </h3>
                <p className="mt-4 text-slate-600 leading-[1.8] text-[15px] md:text-[17px]">
                  Reliable supply house for electrical, telecom, lighting and industrial power solutions.
                </p>

                <div className="mt-5 space-y-3">
                  {[
                    "Electrical Components",
                    "Telecom Infrastructure",
                    "Power & UPS Systems",
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[9px]">
                        <i className="fas fa-check" />
                      </div>
                      <span className="text-slate-700 font-semibold text-sm md:text-base">
                        {item}
                      </span>
                    </div>
                  ))}
                </div>

                <Link
                  to="/trading"
                  className="inline-flex items-center justify-center gap-3 mt-6 w-full px-6 py-4 rounded-2xl bg-gradient-to-r from-blue-700 to-cyan-500 text-white font-bold hover:scale-[1.02] transition-all duration-300 shadow-xl text-sm md:text-base"
                >
                  Explore Trading
                  <i className="fas fa-arrow-right" />
                </Link>
              </div>
            </div>
          </div>

          {/* QUICK STATS */}
          <div className="grid grid-cols-3 gap-4 mt-8 max-w-3xl mx-auto">
            {[
              ["15+", "Years Experience"],
              ["500+", "Our Clients"],
              ["100K+", "Shipments"],
            ].map(([number, label]) => (
              <div
                key={label}
                className="bg-white border border-slate-100 rounded-3xl py-5 text-center shadow-lg"
              >
                <div className="text-3xl md:text-4xl font-black bg-gradient-to-r from-blue-700 to-cyan-500 bg-clip-text text-transparent">
                  {number}
                </div>
                <div className="mt-2 text-[10px] md:text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">
                  {label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========================= */}
      {/* CONTACT SECTION */}
      {/* ========================= */}
      <section
        id="contact"
        className="relative py-24 bg-gradient-to-b from-white to-slate-50 overflow-hidden"
      >
        <div className="absolute top-[-150px] left-[-100px] w-[400px] h-[400px] bg-blue-200/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-150px] right-[-100px] w-[400px] h-[400px] bg-cyan-200/20 rounded-full blur-[120px]" />

        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-blue-50 border border-blue-100 text-blue-700 font-bold uppercase tracking-[0.18em] text-xs">
              Contact Us
            </div>

            <h2 className="mt-7 text-[38px] md:text-[56px] font-black leading-[1.05] tracking-[-3px] text-slate-900">
              Send Your
              <span className="block bg-gradient-to-r from-blue-700 to-cyan-500 bg-clip-text text-transparent">
                Business Inquiry
              </span>
            </h2>

            <p className="mt-6 text-slate-600 text-lg leading-[1.9]">
              Reach out to BAJABAR INCORPORATION FZE for logistics, freight forwarding, industrial supply, telecom and power infrastructure solutions.
            </p>
          </div>

          <div className="mt-16 grid lg:grid-cols-2 gap-10">
            {/* LEFT INFO */}
            <div className="space-y-6">
              <div className="bg-white rounded-[32px] border border-slate-200 p-7 shadow-[0_15px_45px_rgba(0,0,0,0.06)]">
                <div className="flex items-start gap-5">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-700 to-cyan-500 text-white flex items-center justify-center shadow-xl">
                    <i className="fas fa-user-tie text-lg" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-black text-slate-900">
                      Zubair Bin Mohammed
                    </h3>
                    <div className="mt-1 text-blue-700 font-semibold">
                      Managing Director
                    </div>
                    <div className="mt-4 text-slate-600 leading-8">
                      zubair@bajabarinc.ae
                      <br />
                      +971 525131571
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-[32px] border border-slate-200 p-7 shadow-[0_15px_45px_rgba(0,0,0,0.06)]">
                <div className="flex items-start gap-5">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-700 to-cyan-500 text-white flex items-center justify-center shadow-xl">
                    <i className="fas fa-user-tie text-lg" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-black text-slate-900">
                      Mohammed A. Mannan
                    </h3>
                    <div className="mt-1 text-blue-700 font-semibold">
                      Business Development
                    </div>
                    <div className="mt-4 text-slate-600 leading-8">
                      mohammed@bajabarinc.ae
                      <br />
                      +91 81252 11886
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-700 to-cyan-500 rounded-[32px] p-8 text-white shadow-[0_20px_60px_rgba(37,99,235,0.3)]">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center">
                    <i className="fas fa-map-marker-alt text-xl" />
                  </div>
                  <div>
                    <div className="uppercase tracking-[0.2em] text-xs font-bold text-blue-100">
                      Office Location
                    </div>
                    <div className="mt-2 text-2xl font-black">
                      SAIF Zone, Sharjah, UAE
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT FORM */}
            <div className="bg-white rounded-[40px] border border-slate-200 shadow-[0_25px_60px_rgba(0,0,0,0.08)] p-8 md:p-10">
              <h3 className="text-3xl font-black text-slate-900">
                Get In Touch
              </h3>
              <p className="mt-3 text-slate-600">
                Fill the form and our team will contact you shortly.
              </p>
              <div className="mt-8">
                <ContactForm
                  division="trading"
                  services={[
                    "Custom Clearance",
                    "Logistics",
                    "Freight Forwarding",
                    "Electrical Supply",
                    "Telecom",
                    "Power Solutions",
                    "Other",
                  ]}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========================= */}
      {/* MAP SECTION */}
      {/* ========================= */}
      <section className="relative py-20 bg-white overflow-hidden">
        <div className="absolute top-[-120px] left-[-120px] w-[350px] h-[350px] bg-blue-200/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-120px] right-[-120px] w-[350px] h-[350px] bg-cyan-200/20 rounded-full blur-[120px]" />

        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-blue-50 border border-blue-100 text-blue-700 font-bold uppercase tracking-[0.18em] text-xs">
              Our Location
            </div>

            <h2 className="mt-7 text-[38px] md:text-[56px] font-black leading-[1.05] tracking-[-3px] text-slate-900">
              Visit Our
              <span className="block bg-gradient-to-r from-blue-700 to-cyan-500 bg-clip-text text-transparent">
                UAE Office
              </span>
            </h2>

            <p className="mt-6 text-slate-600 text-lg leading-[1.9]">
              BAJABAR INCORPORATION FZE is located in SAIF Zone, Sharjah, UAE.
            </p>
          </div>

          <div className="mt-14 rounded-[36px] overflow-hidden border border-slate-200 shadow-[0_25px_60px_rgba(0,0,0,0.08)] bg-white">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3606.3045877990335!2d55.497321500000005!3d25.327558299999996!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f5f8f1e46d7dd%3A0xd03ed01c90ceb51b!2sBAJABAR%20INCORPORATION%20FZE!5e0!3m2!1sen!2sae!4v1779453969057!5m2!1sen!2sae"
              width="100%"
              height="500"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="BAJABAR Location"
              className="w-full"
            />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="relative overflow-hidden bg-gradient-to-br from-[#0a1f57] via-[#0d2c78] to-[#1b3f98] text-white pt-7 pb-4">
        <div className="absolute top-[-150px] right-[-100px] w-[400px] h-[400px] bg-cyan-400/10 rounded-full blur-[120px]" />

        <div className="max-w-7xl mx-auto px-5 md:px-6 relative z-10">
          <div className="grid md:grid-cols-3 gap-6 border-b border-white/10 pb-6">
            <div className="flex flex-col items-start">
              <div className="inline-flex items-center justify-center bg-white/95 rounded-2xl px-4 py-2 shadow-lg">
                <img
                  src="/logo.png"
                  alt="BAJABAR Logo"
                  className="h-16 md:h-20 lg:h-24 w-auto object-contain drop-shadow-xl hover:scale-105 transition-all duration-500"
                />
              </div>

              <h3 className="mt-3 text-2xl font-black leading-tight text-white">
                BAJABAR INCORPORATION FZE
              </h3>

              <p className="mt-3 text-[15px] leading-7 text-blue-100/80 max-w-sm">
                Trusted trading and logistics partner delivering freight forwarding, customs clearance, telecom, industrial supply and power solutions.
              </p>
            </div>

            <div>
              <h4 className="text-xl font-bold text-white mb-5">
                Quick Links
              </h4>
              <div className="flex flex-col gap-4">
                <Link
                  to="/logistics"
                  className="text-blue-100/80 hover:text-white transition duration-300"
                >
                  Logistics Division
                </Link>
                <Link
                  to="/trading"
                  className="text-blue-100/80 hover:text-white transition duration-300"
                >
                  Trading Division
                </Link>
                <a
                  href="#contact"
                  onClick={handleContactClick}
                  className="text-blue-100/80 hover:text-white transition duration-300"
                >
                  Contact Us
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-xl font-bold text-white mb-5">
                Contact Info
              </h4>
              <div className="space-y-5 text-blue-100/85">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-2xl bg-white/10 backdrop-blur-xl flex items-center justify-center">
                    <i className="fas fa-map-marker-alt text-cyan-300" />
                  </div>
                  <span className="leading-7">SAIF Zone, Sharjah, UAE</span>
                </div>

                <a
                  href="tel:+97165526501"
                  className="flex items-center gap-4 hover:text-white transition duration-300"
                >
                  <div className="w-11 h-11 rounded-2xl bg-white/10 backdrop-blur-xl flex items-center justify-center">
                    <i className="fas fa-phone-alt text-cyan-300" />
                  </div>
                  <span>+971 6 5526501</span>
                </a>

                <a
                  href="mailto:sales@bajabarinc.ae"
                  className="flex items-center gap-4 hover:text-white transition duration-300"
                >
                  <div className="w-11 h-11 rounded-2xl bg-white/10 backdrop-blur-xl flex items-center justify-center">
                    <i className="fas fa-envelope text-cyan-300" />
                  </div>
                  <span>sales@bajabarinc.ae</span>
                </a>
              </div>
            </div>
          </div>

          <div className="pt-5 flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-blue-100/70">
            <div>
              © 2026 BAJABAR INCORPORATION FZE. All rights reserved.
            </div>
            <div>
              Designed & Developed by{" "}
              <a
                href="https://crescentitsolutions.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyan-300 hover:text-white font-semibold transition duration-300"
              >
                Crescent IT Solutions INC IL, USA
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}